/**
 * CheckoutContext - 결제 플로우 상태 관리
 *
 * 결제 진행 상태, 테이블 주문, 주문 데이터 등 결제 관련 상태를 제공합니다.
 * 결제 페이지, 영수증, 회사 선택 페이지에서 사용됩니다.
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { PaymentFlow, Company, CartItem } from '@/types';
import type { InvoiceData } from '@/components/invoice/types';
import { ROUTES } from '@/config/routes';
import { logger } from '@/utils/logger';

/**
 * PaymentData - 결제 데이터 인터페이스
 *
 * 주문 생성, 결제 완료, 영수증 표시 등 결제 플로우 전반에서 사용되는 데이터 타입입니다.
 * 모든 필드가 명시적으로 정의되어 있어 타입 안전성을 보장합니다.
 */
export interface PaymentData {
  // OrderData 호환 필드
  orderId?: string;
  orderDbId?: number;
  paymentId?: number;
  guestPassword?: string;
  timestamp?: Date;
  total?: number;
  // PaymentData 확장 필드
  restaurant?: string;
  /** GROSS 합계 (IVA 포함) — #299 에서 `subtotal` 에서 변경. API `Order.gross_subtotal` 매핑 */
  grossSubtotal?: number;
  /** NET 소계 (#284) — 영수증 Subtotal 표시용. API `Order.net_subtotal`을 transformer에서 camelCase로 매핑 */
  netSubtotal?: number;
  tax?: number;
  tip?: number;
  paymentMethod?: string;
  showPaymentProcessing?: boolean;
  gateway?: 'bloomin' | 'manual';
  bloominInitiateUrl?: string;
  // PaymentResult 호환 필드
  amount?: number;
  status?: 'success' | 'failed';
  paymentFlow?: string;
  items?: CartItem[];
  pointsUsed?: number;
  pointsDiscount?: number;
  tableNumber?: string;
  // 추가 필드 (명시적 타입 정의)
  specialInstructions?: string;
  couponCode?: string;
  /**
   * #285 stale 방지: 주문 시점 세율(비율)·통화 스냅샷.
   * 영수증/세금계산서 화면이 매장 컨텍스트 대신 이 값을 우선 사용.
   */
  taxRate?: number;
  currency?: string;
  /**
   * 결제 시점 축 스냅샷 (#647 C1 — combination.timing).
   * 로컬 주문 snapshot 이 미결제 후불(latestPayment)을 보존해 OrderHistoryPage 의
   * 후불 결제 진입 게이트가 API 조회 없이도 동작하도록 한다.
   */
  paymentTiming?: 'prepay' | 'postpay';
  /** 결제 조합 코드 스냅샷 (#647 C1 — combination.code, 예: 'cash_manual_postpay') */
  combinationCode?: string;
}

interface CheckoutContextValue {
  // 결제 상태
  paymentFlow: PaymentFlow;
  orderData: PaymentData | null;
  selectedCardForPayment: string | null;
  invoiceData: InvoiceData | null;
  selectedCompany: Company | null;

  // URL 기반 상태 (읽기 전용)
  showPayment: boolean;
  showCompanyList: boolean;

  // 테이블 주문 상태
  // (#414) isTableOrder/currentTable dead state 제거 — 테이블 컨텍스트 단일 출처는
  // URL/sessionStorage 파생 (src/lib/tableContext.ts 박제 D + useRestaurants).
  orderedItems: CartItem[];
  orderedItemCount: number;
  orderedTotal: number;

  // 결제 액션
  openPayment: () => void;
  closePayment: () => void;
  handlePaymentSuccess: (data: PaymentData) => void;
  handlePaymentClose: () => void;
  handleProcessingError: () => void;
  handleRetryPayment: (cardId: string) => void;
  handleAddNewCard: () => void;

  // 세금계산서 액션
  handleTaxInvoiceRequest: () => void;
  handleCompanyListBack: () => void;
  handleCompanySelect: (company: Company) => void;
  handleTaxInvoiceComplete: (invoiceData: InvoiceData) => void;
  handleInvoiceClose: () => void;
  handleCompleteClose: () => void;

  // 테이블 주문 액션
  addOrderedItems: (items: CartItem[]) => void;
  toggleOrderExpand: (orderId: string) => void;
  expandedOrders: string[];

  // 상태 초기화
  setPaymentFlow: (flow: PaymentFlow) => void;
  setOrderData: (data: PaymentData | null) => void;
  setInvoiceData: (data: InvoiceData | null) => void;
  resetCheckoutState: () => void;
}

const CheckoutContext = createContext<CheckoutContextValue | null>(null);

interface CheckoutProviderProps {
  children: ReactNode;
}

export function CheckoutProvider({ children }: CheckoutProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();

  // URL 기반 상태
  const showPayment = location.pathname === ROUTES.PAYMENT;
  const showCompanyList = location.pathname === ROUTES.COMPANY_LIST;

  // 결제 상태
  const [paymentFlow, setPaymentFlow] = useState<PaymentFlow>('idle');
  const [orderData, setOrderData] = useState<PaymentData | null>(null);
  const [selectedCardForPayment, setSelectedCardForPayment] = useState<string | null>(null);
  const [invoiceData, setInvoiceData] = useState<InvoiceData | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  // 테이블 주문 상태
  const [orderedItems, setOrderedItems] = useState<CartItem[]>([]);
  const [expandedOrders, setExpandedOrders] = useState<string[]>([]);

  // 네비게이션 헬퍼
  const goBack = useCallback(
    (fallback: string) => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(fallback, { replace: true });
      }
    },
    [navigate]
  );

  // 결제 페이지 열기/닫기
  const openPayment = useCallback(() => {
    navigate(ROUTES.PAYMENT);
  }, [navigate]);

  const closePayment = useCallback(() => {
    // `/payment` 모달이 실제로 열려 있을 때만 뒤로가기로 닫는다.
    //
    // 배경 (후불 주문 완료 시 빈 /cart 로 튕기던 회귀):
    // CheckoutPage 직접 흐름(후불/현금)은 `/checkout` 에서 주문을 생성하고
    // `handlePaymentSuccess` 를 호출하는데, 이 함수가 무조건 `closePayment()` 를
    // 호출했다. `goBack` 은 히스토리가 있으면 `navigate(-1)` 이므로
    // [메뉴, /cart, /checkout] 스택에서 한 칸 뒤(/cart 빈 카트)로 pop 되어
    // 직후의 `setShowOrderSuccess(true)`(OrderSuccessScreen)를 우회했다.
    // `/payment` 게이트웨이 모달을 닫는 본래 용도일 때만 동작하도록 가드한다.
    if (location.pathname === ROUTES.PAYMENT) {
      goBack(ROUTES.HOME);
    }
  }, [goBack, location.pathname]);

  // 결제 성공 처리
  // #687 fail-closed: showPaymentProcessing 기반 'processing' 진입 제거.
  // 게이트웨이 카드 결제에서 이 분기가 모의 결제 상태머신('processing' → 시뮬레이션 →
  // 'success' → ReceiptPage)을 무장시켜, 3DS 화면 뒤로가기 복귀 시 서버 확정(paid /
  // challenge 완주 콜백) 없이 거짓 '결제 완료' 화면이 렌더됐다. 게이트웨이 결제의
  // 완료 화면 도달은 /payment/result(서버 outcome) 경로만 허용한다.
  const handlePaymentSuccess = useCallback(
    (paymentDetails: PaymentData) => {
      logger.info('결제 플로우 시작:', paymentDetails);
      setOrderData(paymentDetails);
      setSelectedCardForPayment(paymentDetails.paymentMethod ?? null);
      closePayment();
    },
    [closePayment]
  );

  // 결제 닫기
  const handlePaymentClose = useCallback(() => {
    closePayment();
    setPaymentFlow('idle');
    setOrderData(null);
  }, [closePayment]);

  // #687: handleProcessingSuccess('success' 진입) 제거 — 클라이언트 시뮬레이션이
  // 결제 성공을 선언하던 유일한 통로. 'success'(ReceiptPage) 진입은 과거 주문
  // 영수증 열람(useReceiptActions, 서버 주문 데이터 기반)만 남긴다.

  // 결제 오류
  const handleProcessingError = useCallback(() => {
    setPaymentFlow('error');
  }, []);

  // 결제 재시도 (#687 fail-closed: 모의 'processing' 재진입 금지)
  // 에러 모달을 닫고 체크아웃으로 복귀시킨다. 실제 재시도는 CheckoutPage 의 결제
  // 버튼(orphan order 재사용 → 서버 /charge reuse 계약)으로만 수행한다.
  const handleRetryPayment = useCallback((cardId: string) => {
    setSelectedCardForPayment(cardId);
    setPaymentFlow('idle');
  }, []);

  // 새 카드 등록 (#687 P1 시정: 'processing' 진입이 모의 시뮬레이션을 거쳐 서버
  // 확정 없는 거짓 '결제 완료'(ReceiptPage)에 도달하던 경로 — idle 복귀로 박제)
  const handleAddNewCard = useCallback(() => {
    logger.info('새 카드 등록 요청 — 결제 플로우 초기화 후 체크아웃 복귀 (#687)');
    setPaymentFlow('idle');
  }, []);

  // 세금계산서 요청
  const handleTaxInvoiceRequest = useCallback(() => {
    navigate(ROUTES.COMPANY_LIST);
  }, [navigate]);

  // 회사 목록 뒤로가기
  const handleCompanyListBack = useCallback(() => {
    goBack(ROUTES.PAYMENT);
  }, [goBack]);

  // 회사 선택
  const handleCompanySelect = useCallback(
    (company: Company) => {
      setSelectedCompany(company);
      goBack(ROUTES.PAYMENT);
      setPaymentFlow('invoice');
    },
    [goBack]
  );

  // 세금계산서 완료
  const handleTaxInvoiceComplete = useCallback((invoiceDetails: InvoiceData) => {
    setInvoiceData(invoiceDetails);
    setPaymentFlow('complete');
  }, []);

  // 세금계산서 닫기
  const handleInvoiceClose = useCallback(() => {
    setPaymentFlow('idle');
    setInvoiceData(null);
  }, []);

  // 완료 닫기
  const handleCompleteClose = useCallback(() => {
    setPaymentFlow('idle');
    setOrderData(null);
    setInvoiceData(null);
  }, []);

  // 주문 아이템 추가
  const addOrderedItems = useCallback((items: CartItem[]) => {
    setOrderedItems((prev) => [...prev, ...items]);
  }, []);

  // 주문 확장/축소
  const toggleOrderExpand = useCallback((orderId: string) => {
    setExpandedOrders((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  }, []);

  // 전체 상태 초기화
  const resetCheckoutState = useCallback(() => {
    setPaymentFlow('idle');
    setOrderData(null);
    setSelectedCardForPayment(null);
    setInvoiceData(null);
    setSelectedCompany(null);
    setOrderedItems([]);
    setExpandedOrders([]);
  }, []);

  // 계산된 값
  const orderedItemCount = useMemo(
    () => orderedItems.reduce((total, item) => total + item.quantity, 0),
    [orderedItems]
  );

  const orderedTotal = useMemo(
    () => orderedItems.reduce((total, item) => total + item.price * item.quantity, 0),
    [orderedItems]
  );

  // Context 값 메모이제이션
  const value = useMemo<CheckoutContextValue>(
    () => ({
      // 결제 상태
      paymentFlow,
      orderData,
      selectedCardForPayment,
      invoiceData,
      selectedCompany,
      showPayment,
      showCompanyList,

      // 테이블 주문 상태
      orderedItems,
      orderedItemCount,
      orderedTotal,
      expandedOrders,

      // 결제 액션
      openPayment,
      closePayment,
      handlePaymentSuccess,
      handlePaymentClose,
      handleProcessingError,
      handleRetryPayment,
      handleAddNewCard,

      // 세금계산서 액션
      handleTaxInvoiceRequest,
      handleCompanyListBack,
      handleCompanySelect,
      handleTaxInvoiceComplete,
      handleInvoiceClose,
      handleCompleteClose,

      // 테이블 주문 액션
      addOrderedItems,
      toggleOrderExpand,

      // 상태 설정
      setPaymentFlow,
      setOrderData,
      setInvoiceData,
      resetCheckoutState,
    }),
    [
      paymentFlow,
      orderData,
      selectedCardForPayment,
      invoiceData,
      selectedCompany,
      showPayment,
      showCompanyList,
      orderedItems,
      orderedItemCount,
      orderedTotal,
      expandedOrders,
      openPayment,
      closePayment,
      handlePaymentSuccess,
      handlePaymentClose,
      handleProcessingError,
      handleRetryPayment,
      handleAddNewCard,
      handleTaxInvoiceRequest,
      handleCompanyListBack,
      handleCompanySelect,
      handleTaxInvoiceComplete,
      handleInvoiceClose,
      handleCompleteClose,
      addOrderedItems,
      toggleOrderExpand,
      resetCheckoutState,
    ]
  );

  return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>;
}

/**
 * useCheckout - CheckoutContext 사용 훅
 *
 * @throws CheckoutProvider 외부에서 사용 시 에러
 */
export function useCheckout(): CheckoutContextValue {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }
  return context;
}

/**
 * usePaymentFlow - 결제 플로우 상태만 사용
 */
export function usePaymentFlow() {
  const {
    paymentFlow,
    orderData,
    selectedCardForPayment,
    showPayment,
    openPayment,
    closePayment,
    handlePaymentSuccess,
    handlePaymentClose,
    handleProcessingError,
    handleRetryPayment,
    handleAddNewCard,
    setPaymentFlow,
    setOrderData,
  } = useCheckout();

  const handlePaymentFlowReset = useCallback(() => setPaymentFlow('idle'), [setPaymentFlow]);

  return {
    paymentFlow,
    orderData,
    selectedCardForPayment,
    showPayment,
    openPayment,
    closePayment,
    handlePaymentSuccess,
    handlePaymentClose,
    handleProcessingError,
    handleRetryPayment,
    handleAddNewCard,
    handlePaymentFlowReset,
    setPaymentFlow,
    setOrderData,
  };
}

/**
 * useTableOrder - 테이블 주문 상태만 사용
 */
export function useTableOrder() {
  const {
    orderedItems,
    orderedItemCount,
    orderedTotal,
    expandedOrders,
    addOrderedItems,
    toggleOrderExpand,
  } = useCheckout();

  return {
    orderedItems,
    orderedItemCount,
    orderedTotal,
    expandedOrders,
    addOrderedItems,
    toggleOrderExpand,
  };
}

/**
 * useInvoice - 세금계산서 상태만 사용
 */
export function useInvoice() {
  const {
    invoiceData,
    selectedCompany,
    showCompanyList,
    handleTaxInvoiceRequest,
    handleCompanyListBack,
    handleCompanySelect,
    handleTaxInvoiceComplete,
    handleInvoiceClose,
    handleCompleteClose,
    setInvoiceData,
  } = useCheckout();

  return {
    invoiceData,
    selectedCompany,
    showCompanyList,
    handleTaxInvoiceRequest,
    handleCompanyListBack,
    handleCompanySelect,
    handleTaxInvoiceComplete,
    handleInvoiceClose,
    handleCompleteClose,
    setInvoiceData,
  };
}
